import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import ellipsize from 'ellipsize';
import { removeEntryCollectionCircularReferences } from 'lib/contentful';
import {
  GetStaticPropsContext,
  GetStaticPaths,
  InferGetStaticPropsType,
} from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { getAllMenuPage } from 'repos/menu';
import { getAllPosts, getPostBySlug, getRelatedPosts } from 'repos/posts';

import SubscribeCTABlock from 'components/SubscribeCTABlock';
import Layout from 'components/layout';
import PostPreviews from 'components/postPreviews';
import RichTextRenderer from 'components/richTextRenderer';
import SEO from 'components/seo';
import ClockIcon from 'components/svgs/clock';
import FacebookIcon from 'components/svgs/facebook';
import LinkedinIcon from 'components/svgs/linkedin';
import ShareIcon from 'components/svgs/share';
import TwitterIcon from 'components/svgs/twitter';

import { getUrl } from 'utils/env';

export default function PostPage({
  post,
  relatedPosts,
  menuParams,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { t } = useTranslation();
  const { locale, asPath } = useRouter();
  const localizedSlug = post.fields.localizedArticle?.fields.slug;

  const siteUrl = getUrl();
  const url = `${siteUrl}/${locale}${asPath}`;
  const imageUrl = `https:${post.fields.image?.fields.file.url}`;
  const description = ellipsize(
    // @ts-ignore
    documentToPlainTextString(post.fields.content),
    160
  );
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'BlogPosting',
    url,
    headline: post.fields.title,
    name: post.fields.title,
    description,
    ...(post.fields.author
      ? {
          author: {
            '@type': 'Person',
            name: `${post.fields.author.fields.firstName} ${post.fields.author.fields.lastName}`,
          },
          creator: [
            `${post.fields.author.fields.firstName} ${post.fields.author.fields.lastName}`,
          ],
        }
      : {}),
    publisher: {
      '@type': 'Organization',
      name: 'apprentx',
      url: siteUrl,
    },
    image: imageUrl,
    mainEntityOfPage: url,
  };

  return (
    <Layout
      stickyHeader
      overridePath={localizedSlug || 'blog'}
      menuParams={menuParams}
    >
      <SEO
        title={post.fields.title}
        image={imageUrl}
        description={description}
        schema={schema}
      />
      <section className="pb-24 pt-12">
        <article>
          <div className="mx-auto max-w-3xl px-6">
            <h1 className="text-r-4xl mb-6 font-extrabold">
              {post.fields.title}
            </h1>
            {post.fields.author && (
              <div className="mb-4 flex items-center">
                <div className="mr-3" style={{ fontSize: 0 }}>
                  <Image
                    src={`https:${post.fields.author.fields.image.fields.file.url}?w=40&h=40&fit=fill&f=face`}
                    alt="Author"
                    width={40}
                    height={40}
                    className="rounded-full bg-gray-200"
                  />
                </div>
                <div className="text-r-lg font-bold">
                  {t('by_x', {
                    name: `${post.fields.author.fields.firstName} ${post.fields.author.fields.lastName}`,
                  })}
                </div>
              </div>
            )}
            <div className="mb-8 flex space-x-8 text-sm">
              {/* TODO re-enabled when we have more articles */}
              {/* {(post.fields.labels || []).length > 0 && (
                <div className="font-bold text-gray-500">
                  {post.fields.labels
                    ?.map((label) => label.fields.name)
                    .join(" | ")}
                </div>
              )} */}
              <div className="flex items-center text-sm text-gray-500">
                <ClockIcon width={16} className="mr-2" />
                {t('x_min', { x: post.fields.readingTime })}
              </div>
            </div>
          </div>
          <div className="mx-auto mb-8 max-w-3xl md:px-6">
            <Image
              src={`${imageUrl}?fm=webp`}
              alt={t('cover_image')}
              width={post.fields.image.fields.file.details.image?.width}
              height={post.fields.image.fields.file.details.image?.height}
              layout="responsive"
              className="bg-gray-100 md:rounded-xl "
            />
          </div>
          <div className="px-6">
            <div className="mx-auto max-w-[70ch]">
              <RichTextRenderer document={post.fields.content} />
              <div className="mt-8 flex">
                <div className="border-grey-400 mr-8 border-r pr-8 text-gray-500">
                  <ShareIcon width={24} />
                </div>
                <div className="flex space-x-8 text-gray-500">
                  <a
                    href={`https://facebook.com/sharer/sharer.php?u=${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('share_on_x', { x: 'Facebook' })}
                  >
                    <FacebookIcon width={24} />
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet/?text=${encodeURIComponent(
                      post.fields.title
                    )}&url=${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('share_on_x', { x: 'Twitter' })}
                  >
                    <TwitterIcon width={24} />
                  </a>
                  <a
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${encodeURIComponent(
                      post.fields.title
                    )}&summary=${encodeURIComponent(
                      post.fields.title
                    )}&source=${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('share_on_x', { x: 'LinkedIn' })}
                  >
                    <LinkedinIcon width={24} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </section>
      <section className="bg-gray-100 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-r-3xl mb-12 font-bold">{t('more_articles')}</h2>
          <PostPreviews posts={relatedPosts} />
        </div>
      </section>
      <SubscribeCTABlock text="Faites partie de la communauté Apprentx!" />
    </Layout>
  );
}

export const getStaticProps = async ({
  locale,
  params,
}: GetStaticPropsContext) => {
  const slug = params?.slug;

  if (typeof slug !== 'string') {
    throw new Error('Invalid slug param');
  }

  const post = removeEntryCollectionCircularReferences(
    await getPostBySlug(slug, { locale })
  ).items[0];
  const relatedPosts = removeEntryCollectionCircularReferences(
    await getRelatedPosts(post.sys.id, { locale })
  ).items.filter((post) => post.fields.locale === locale);

  const menuParams = await getAllMenuPage({ locale });

  return {
    props: {
      post,
      relatedPosts,
      menuParams,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const posts = removeEntryCollectionCircularReferences(
    await getAllPosts({ preview: false })
  ).items;

  const paths = (locales || []).flatMap((locale) =>
    posts.map((post) => ({
      locale,
      params: {
        // @ts-ignore
        slug: post.fields.slug,
      },
    }))
  );

  return {
    paths,
    fallback: false,
  };
};
